import React, { useEffect, useState } from 'react'
import { FormatInvoiceDecimalNumber, getValueFromResource } from '../../../common/timeLiveFunction';
import { t } from "i18next";
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { Doughnut } from 'react-chartjs-2';
import { Chart, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import Loader from '../../../common/Loader';
import fetchTimeliveAPI from '../../../common/fetchTimeliveAPI';
import SummaryBoxes from './SummaryBoxes';
import { updateIsSummaryLoading } from './BoardSlice';

Chart.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale);
const Summary = (props: any) => {
    const { projectSummary, AccountProjectId, isParentLoading, projectSummaryData } = props;
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useAppDispatch();
    const [summaryData, setSummaryData] = useState<any>({});
    const { isSummaryLoading } = useAppSelector((state) => state.board);
    const { cultureInfoName, decimalPlaces } = useAppSelector((state) => state?.globalState);

    const getPriorityColor = (priorityName: string): string => {
        const defaultColors: Record<string, string> = {
            Urgent: '#dc3545', // Red
            High: '#ffc107',   // Yellow
            Medium: '#0d6efd', // Blue
            Low: '#6c757d',    // Grey
        };

        if (defaultColors[priorityName]) {
            return defaultColors[priorityName];
        }

        const darkPalette = [
            '#2c3e50', '#8e44ad', '#d35400',
            '#16a085', '#27ae60', '#7f8c8d', '#8B4513',
            '#483D8B', '#556B2F', '#008B8B', '#800080',
            '#A52A2A', '#2F4F4F'
        ];

        let hash = 0;
        for (let i = 0; i < priorityName.length; i++) {
            hash = priorityName.charCodeAt(i) + ((hash << 5) - hash);
        }

        const index = Math.abs(hash) % darkPalette.length;
        return darkPalette[index];
    };

    const getGraphData = async () => {
        try {
            setIsLoading(true);
            const response = await fetchTimeliveAPI(`api/Project/GetAccountProjectTaskGraphData/${AccountProjectId}`, 'GET', "", true);
            const json = await response.json();
            setSummaryData(json?.Data || {});
            setIsLoading(false);
            dispatch(updateIsSummaryLoading(false))
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        getGraphData();
    }, [AccountProjectId, isSummaryLoading])

    // const labels = Object.keys(statusCounts);
    // const values = Object.values(statusCounts);
    const labels = summaryData?.TaskStatusGraph?.map((x: any) => x.Status) || [];
    const values = summaryData?.TaskStatusGraph?.map((x: any) => x.TaskStatusCount) || [];

    const total = values?.reduce((a: any, b: any) => a + b, 0);
    const data = {
        labels,
        datasets: [
            {
                label: 'Tasks by Status',
                data: values,
                backgroundColor: ['#008978', '#67266D', '#FFB000', '#0C66E4'],
                borderColor: '#fff',
                borderWidth: 2,
                cutout: '70%',
            },
        ],
    };

    const options: any = {
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: (context: any) => `${context.label}: ${context.raw}`,
                },
            },
        },
    };

    const priorityLabels = summaryData?.TaskPriorityGraph?.map((x: any) => x.Priority) || [];//Object.keys(priorityCounts);
    const priorityValues = summaryData?.TaskPriorityGraph?.map((x: any) => x.TaskPriorityCount) || [];//Object.values(priorityCounts);
    const priorityColors = priorityLabels.map((label: string) => getPriorityColor(label));

    const priorityData = {
        labels: priorityLabels,
        datasets: [
            {
                label: 'Task Priority',
                data: priorityValues,
                backgroundColor:priorityColors, //['#EE3853', '#FFB000', '#0C66E4', '#192730'],
                borderRadius: 6,
                barThickness: 20,
            },
        ],
    };

    const barOptions: any = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (context: any) => `${context.label}: ${context.raw}`,
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { color: '#192730', font: { size: 12, weight: '500' } },
            },
            y: {
                beginAtZero: true,
                grid: { color: '#e5e7eb' },
                ticks: { stepSize: 1, color: '#6b7280', font: { size: 12 } },
            },
        },
    };

    const typeLabels = summaryData?.TaskTypeGraph?.map((x: any) => x.TaskType) || [];//Object.keys(typeCounts);
    const typeValues = summaryData?.TaskTypeGraph?.map((x: any) => x.TaskTypeCount) || [];//Object.values(typeCounts);

    const typeData = {
        labels: typeLabels,
        datasets: [
            {
                label: 'Task Type',
                data: typeValues,
                backgroundColor: ['#192730', '#EE3853', '#FFB000'],
                borderRadius: 6,
                barThickness: 25,
            },
        ],
    };

    const maxTaskTypeCount = Math.max(...typeValues, 0);

const horizontalBarOptions: any = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { display: false },
        tooltip: {
            callbacks: {
                label: (context: any) => `${context.label}: ${context.raw}`,
            },
        },
    },
    scales: {
        x: {
            beginAtZero: true,
            grid: { color: '#e5e7eb' },
            ticks: {
                color: '#6b7280',
                font: { size: 12 },
                precision: 0,
                stepSize: maxTaskTypeCount <= 20 ? 1 : undefined,
                callback: function(value: any) {
                    if (Math.floor(value) === value) {
                        return value;
                    }
                }
            },
        },
        y: {
            grid: { display: false },
            ticks: { color: '#6b7280', font: { size: 12, weight: '500' } },
        },
    },
};

    // const horizontalBarOptions: any = {
    //     indexAxis: 'y',
    //     responsive: true,
    //     maintainAspectRatio: false,
    //     plugins: {
    //         legend: { display: false },
    //         tooltip: {
    //             callbacks: {
    //                 label: (context: any) => `${context.label}: ${context.raw}`,
    //             },
    //         },
    //     },
    //     scales: {
    //         x: {
    //             beginAtZero: true,
    //             grid: { color: '#e5e7eb' },
    //             ticks: {
    //                 color: '#6b7280',
    //                 font: { size: 12 },
    //                 callback: (value: any) => {
    //                     return FormatInvoiceDecimalNumber(cultureInfoName.toString(), value, decimalPlaces);
    //                 }
    //             },
    //         },
    //         y: {
    //             grid: { display: false },
    //             ticks: { color: '#6b7280', font: { size: 12, weight: '500' } },
    //         },
    //     },
    // };



    const totalTaskStatusCount = summaryData?.TaskStatusGraph?.reduce((sum: any, item: any) => sum + item.TaskStatusCount, 0);
    return (
        <>
            {(isLoading && !isParentLoading) && <Loader />}
            <div className="my-5">
                <SummaryBoxes projectSummary={projectSummary} projectSummaryData={projectSummaryData} />
                <div className="separator my-4"></div>
                <div className="row">
                    <div className="col-md-6">
                        <div className='cardInfoShadowSummary mt-3' style={{ padding: '20px', paddingTop: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#fff' }}>
                            <div className="mb-3">
                                <h3 className="fw-bold headerTitle mb-0" style={{ lineHeight: 1.5 }}>
                                    {getValueFromResource('Status Overview', dispatch, t)}
                                </h3>
                                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                                    {getValueFromResource('Quickly assess the current status of your tasks.', dispatch, t)}
                                    {/* Get a snapshot of the status of your work items. */}
                                </p>
                            </div>

                            <div className="d-flex align-items-center justify-content-around" style={{ maxWidth: '600px' }}>
                                {summaryData?.TaskStatusGraph?.length > 0 ? (
                                    <div className="position-relative mt-2" style={{ width: '220px', height: '220px' }}>
                                        <Doughnut data={data} options={options} />
                                        {Number(totalTaskStatusCount) > 0 && (
                                            <div className="position-absolute top-50 start-50 translate-middle text-center" style={{ pointerEvents: 'none' }}>
                                                <div className="fw-bold text-dark" style={{ fontSize: '48px', lineHeight: '1' }}> {String(totalTaskStatusCount)}</div>
                                                <div className="text-muted" style={{ fontSize: '14px', marginTop: '5px' }}>{getValueFromResource(`Total ${Number(totalTaskStatusCount) > 1 ? "Tasks" : "Task"}`, dispatch, t)}</div>
                                            </div>
                                        )}
                                    </div>

                                ) : (
                                    <div style={{ width: '100%', height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: "center" }}>
                                        <p className='noDataFound'>
                                            {/* {t('No Record Found')} */}
                                            {getValueFromResource('No Record Found', dispatch, t)}
                                        </p>
                                    </div>
                                )}

                                <div className="d-flex flex-column gap-3 ms-2">
                                    {summaryData?.TaskStatusGraph?.map((item: any, index: any) => (
                                        <div key={index} className="d-flex align-items-center gap-2">
                                            <div
                                                style={{
                                                    width: '12px',
                                                    height: '12px',
                                                    backgroundColor: item.color,
                                                    borderRadius: '2px'
                                                }}
                                            ></div>
                                            <small className="text-dark" style={{ fontSize: '12px' }}>
                                                {item.Status}: <span className="fw-semibold">{item.TaskStatusCount}</span>
                                            </small>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div
                            className="cardInfoShadowSummary my-3"
                            style={{
                                padding: '20px',
                                paddingTop: '12px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                backgroundColor: '#fff',
                                flex: '1 1 45%',
                                // width: '50%'
                            }}
                        >
                            <div className="mb-3">
                                <h3 className="fw-bold headerTitle" style={{ lineHeight: 1.5 }}>
                                    {getValueFromResource('Task Priority Overview', dispatch, t)}
                                </h3>
                                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                                    {getValueFromResource('Analyze tasks based on their priority levels.', dispatch, t)}
                                    {/* Analyze tasks based on their priority levels. */}
                                </p>
                            </div>

                            {summaryData?.TaskPriorityGraph?.length > 0 ? (
                                <div style={{ width: '100%', height: '220px', margin: '1% 0%' }}>
                                    <Bar data={priorityData} options={barOptions} />
                                </div>
                            ) : (
                                <div style={{ width: '100%', height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: "center" }}>
                                    <p className='noDataFound'>
                                        {/* {t('No Record Found')} */}
                                        {getValueFromResource('No Record Found', dispatch, t)}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="row" style={{ marginBottom: '2%' }}>
                    <div className="col-md-6">
                        <div
                            className="cardInfoShadowSummary my-3"
                            style={{
                                padding: '20px',
                                paddingTop: '12px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                backgroundColor: '#fff',
                                // width: '50%',
                            }}
                        >
                            <div className="mb-3">
                                <h3 className="fw-bold headerTitle" style={{ lineHeight: 1.5 }}>
                                    {getValueFromResource('Task Type Overview', dispatch, t)}
                                </h3>
                                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                                    {getValueFromResource('Understand distribution of tasks by type.', dispatch, t)}
                                    {/* Understand distribution of tasks by type. */}
                                </p>
                            </div>
                            {summaryData?.TaskPriorityGraph?.length > 0 ? (
                                <div style={{ width: '100%', height: '220px' }}>
                                    <Bar data={typeData} options={horizontalBarOptions} />
                                </div>
                            ) : (
                                <div style={{ width: '100%', height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: "center" }}>
                                    <p className='noDataFound'>
                                        {/* {t('No Record Found')} */}
                                        {getValueFromResource('No Record Found', dispatch, t)}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Summary;